import {
  ArrowRight,
  ClockCounterClockwise,
  Stack,
} from '@phosphor-icons/react';
import { Spinner } from '@/components/ui/spinner';
import ScheduledQuestionPreviewModal from '@/components/ora/Study/Exam/components/ScheduledQuestionPreviewModal';
import LinkedFlashcardsModal from './LinkedFlashcardsModal';
import type { MissedQuestionBadges } from './useMissedQuestionBadges';

/**
 * What a second "Study Now" on the same question says. The set a first tap minted
 * is the one the server keeps returning (it is idempotent on the question), so a
 * re-curated second ask cannot change it — and the thread scrolling to the old
 * deck without a word would read as the new selection being honoured (ORA-832).
 */
const LINKED_FLASHCARDS_ALREADY_ADDED =
  'Already added — showing what you asked for below.';

/**
 * The badge row under a missed question's explanation, and the refusal that
 * belongs on it when neither modal is open. State: `useMissedQuestionBadges`.
 */
export const MissedQuestionBadgeRow = ({
  badges,
}: {
  badges: MissedQuestionBadges;
}) => {
  const {
    addBlocksUnavailableReason,
    badgeError,
    hasFutureScheduledVariant,
    linkedFlashcardBadgeLabel,
    linkedFlashcardsModalIsOpen,
    openLinkedFlashcards,
    openScheduledVariant,
    pendingBadge,
    scheduledPreviewModalIsOpen,
    showLinkedFlashcardBadge,
    showStudyBadges,
  } = badges;

  return (
    <>
      {showStudyBadges &&
      (hasFutureScheduledVariant || showLinkedFlashcardBadge) ? (
        <div className="sr-badge-container sr-badge-enter">
          {hasFutureScheduledVariant ? (
            <div
              className="sr-badge sr-badge--qbank"
              role="button"
              tabIndex={0}
              aria-label="Open scheduled QBank question preview"
              aria-busy={pendingBadge === 'scheduled_variant' || undefined}
              aria-disabled={addBlocksUnavailableReason ? true : undefined}
              title={addBlocksUnavailableReason}
              onClick={openScheduledVariant}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openScheduledVariant();
                }
              }}
            >
              {pendingBadge === 'scheduled_variant' ? (
                <Spinner size={14} thickness={3} aria-hidden="true" />
              ) : (
                <ClockCounterClockwise />
              )}
              Scheduled 1 QBank Question
              <ArrowRight />
            </div>
          ) : null}
          {showLinkedFlashcardBadge ? (
            <div
              className="sr-badge sr-badge--flashcards"
              role="button"
              tabIndex={0}
              aria-label="Open linked flashcards"
              aria-busy={pendingBadge === 'flashcards' || undefined}
              aria-disabled={addBlocksUnavailableReason ? true : undefined}
              title={addBlocksUnavailableReason}
              onClick={openLinkedFlashcards}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openLinkedFlashcards();
                }
              }}
            >
              {pendingBadge === 'flashcards' ? (
                <Spinner size={14} thickness={3} aria-hidden="true" />
              ) : (
                <Stack />
              )}
              {linkedFlashcardBadgeLabel}
              <ArrowRight />
            </div>
          ) : null}
        </div>
      ) : null}
      {/* Both badges now preview behind a modal, so a refusal shows inside
          the modal the student is looking at. When neither is open — it was
          closed before the answer came back, or the student closed it after
          — the reason belongs on the row rather than nowhere. */}
      {badgeError &&
      !(badgeError.badge === 'flashcards'
        ? linkedFlashcardsModalIsOpen
        : scheduledPreviewModalIsOpen) ? (
        <div className="chat-ui_missing" role="alert">
          {badgeError.message}
        </div>
      ) : null}
    </>
  );
};

/** The two modals the badges open: the variant preview and the card curation. */
export const MissedQuestionBadgeModals = ({
  badges,
  linkedFlashcardsStudyNote,
  vignetteId: vignetteItemId,
}: {
  badges: MissedQuestionBadges;
  linkedFlashcardsStudyNote?: string;
  vignetteId: string;
}) => {
  const {
    addsScheduledVariant,
    badgeError,
    closeLinkedFlashcards,
    linkedFlashcards,
    linkedFlashcardsAlreadyAdded,
    linkedFlashcardsAreGone,
    linkedFlashcardsModalIsOpen,
    onCurationChanged,
    pendingBadge,
    scheduledPreviewModalIsOpen,
    scheduledVariant,
    setLinkedFlashcardWriteIsPending,
    setScheduledPreviewModalIsOpen,
    studyLinkedFlashcardsNow,
    studyScheduledVariantNow,
  } = badges;

  return (
    <>
      <ScheduledQuestionPreviewModal
        isOpen={scheduledPreviewModalIsOpen}
        onClose={() => setScheduledPreviewModalIsOpen(false)}
        content={scheduledVariant?.content}
        dueAt={scheduledVariant?.dueAt}
        onStudyNow={addsScheduledVariant ? studyScheduledVariantNow : undefined}
        isStudyNowPending={pendingBadge === 'scheduled_variant'}
        studyNowError={
          badgeError?.badge === 'scheduled_variant' ? badgeError.message : null
        }
      />
      <LinkedFlashcardsModal
        cards={linkedFlashcards}
        isOpen={linkedFlashcardsModalIsOpen}
        isStudyNowPending={pendingBadge === 'flashcards'}
        onClose={closeLinkedFlashcards}
        onCurationChanged={onCurationChanged}
        onStudyNow={studyLinkedFlashcardsNow}
        onWriteIsPendingChange={setLinkedFlashcardWriteIsPending}
        studyNowError={
          badgeError?.badge === 'flashcards' ? badgeError.message : null
        }
        studyNowNote={linkedFlashcardsStudyNote}
        studyNowNotice={
          linkedFlashcardsAlreadyAdded ? LINKED_FLASHCARDS_ALREADY_ADDED : null
        }
        studyNowUnavailable={linkedFlashcardsAreGone}
        vignetteId={vignetteItemId}
      />
    </>
  );
};
